import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { VerdeSpark } from "./ui-bits";

export const TYPES = ["Apartments", "Student Housing", "Senior Housing", "Co-Living", "Serviced Apartments", "Hotels"];
export const STAGES = ["Land acquisition", "Licensing / planning", "Pre-sales / pre-construction"];

export const TYPES_PT = ["Apartamentos", "Habitação Estudantil", "Habitação Sénior", "Co-Living", "Apartamentos de Serviços", "Hotéis"];
export const STAGES_PT = ["Aquisição de terreno", "Licenciamento / planeamento", "Pré-vendas / pré-construção"];

export const TYPES_NL = ["Appartementen", "Studentenhuisvesting", "Seniorenwoningen", "Co-Living", "Serviced apartments", "Hotels"];
export const STAGES_NL = ["Grondverwerving", "Vergunningen / bestemmingsplan", "Voorverkoop / voorbouwfase"];

export const ANALYSIS_STEPS = [
  "Organising project data",
  "Reviewing market comparables",
  "Mapping planning context",
  "Preparing your indicative brief",
];

export const ANALYSIS_STEPS_PT = [
  "A organizar os dados do projeto",
  "A rever comparáveis de mercado",
  "A mapear o contexto de licenciamento",
  "A preparar o seu resumo indicativo",
];

export const ANALYSIS_STEPS_NL = [
  "Projectgegevens ordenen",
  "Marktvergelijkingen bekijken",
  "Planologische context in kaart brengen",
  "Uw indicatieve samenvatting opstellen",
];

const field =
  "w-full rounded-lg border border-sand bg-cream/60 px-4 py-3 text-sm text-charcoal outline-none transition-colors placeholder:text-fog/60 focus:border-verde-700 focus:bg-white cursor-pointer";

export default function IndicativeTerms({ className = "" }) {
  const navigate = useNavigate();
  const { lang, lp } = useLang();
  const T = {
    en: {
      title: "Get indicative terms",
      note: "Not an offer. You hear back within 24 hours.",
      type: "Project type", typePh: "Select project type",
      amount: "Loan amount (€)", amountPh: "e.g. 2,000,000",
      stage: "Project stage", stagePh: "Select stage",
      location: "Location", locationPh: "e.g. Lisbon, Cascais, Algarve",
      submit: "Submit project",
      types: TYPES, stages: STAGES, steps: ANALYSIS_STEPS,
    },
    pt: {
      title: "Obter condições indicativas",
      note: "Não é uma oferta. Recebe resposta em 24 horas.",
      type: "Tipo de projeto", typePh: "Selecione o tipo de projeto",
      amount: "Montante do crédito (€)", amountPh: "ex.: 2.000.000",
      stage: "Fase do projeto", stagePh: "Selecione a fase",
      location: "Localização", locationPh: "ex.: Lisboa, Cascais, Algarve",
      submit: "Apresentar projeto",
      types: TYPES_PT, stages: STAGES_PT, steps: ANALYSIS_STEPS_PT,
    },
    nl: {
      title: "Indicatieve voorwaarden aanvragen",
      note: "Geen aanbod. U hoort binnen 24 uur van ons.",
      type: "Projecttype", typePh: "Kies een projecttype",
      amount: "Kredietbedrag (€)", amountPh: "bijv. 2.000.000",
      stage: "Projectfase", stagePh: "Kies een fase",
      location: "Locatie", locationPh: "bijv. Lissabon, Cascais, Algarve",
      submit: "Project indienen",
      types: TYPES_NL, stages: STAGES_NL, steps: ANALYSIS_STEPS_NL,
    },
  }[lang];
  const [type, setType] = useState("");
  const [amount, setAmount] = useState("");
  const [stage, setStage] = useState("");
  const [location, setLocation] = useState("");
  const [step, setStep] = useState(-1);
  const timer = useRef(null);

  const ready = type && amount.trim() && stage && location.trim();

  useEffect(() => () => clearInterval(timer.current), []);

  const start = (e) => {
    e.preventDefault();
    if (!ready || step >= 0) return;
    setStep(0);
    let i = 0;
    timer.current = setInterval(() => {
      i += 1;
      if (i >= T.steps.length) {
        clearInterval(timer.current);
        navigate(lp("/apply"), {
          state: { prefill: { location: location.trim(), type, stage, amount: amount.trim() } },
        });
      } else {
        setStep(i);
      }
    }, 700);
  };

  return (
    <div className={`w-full max-w-[440px] rounded-2xl bg-white p-6 sm:p-7 shadow-[0_40px_90px_-30px_rgba(7,39,29,0.45)] ${className}`}>
      <p className="font-display text-xl font-bold text-charcoal">{T.title}</p>
      <p className="mt-1 text-xs text-fog">{T.note}</p>

      {step < 0 ? (
        <motion.form onSubmit={start} className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-ink">{T.type}</span>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="widget-input-type" className={field}>
              <option value="" disabled>{T.typePh}</option>
              {T.types.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-ink">{T.amount}</span>
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={T.amountPh}
                data-testid="widget-input-amount"
                className={field.replace("cursor-pointer", "")}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-ink">{T.stage}</span>
              <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="widget-input-stage" className={field}>
                <option value="" disabled>{T.stagePh}</option>
                {T.stages.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-ink">{T.location}</span>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={T.locationPh}
              data-testid="widget-input-location"
              className={field.replace("cursor-pointer", "")}
            />
          </label>
          <button
            type="submit"
            disabled={!ready}
            data-testid="widget-submit"
            className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-verde-800 px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-verde-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {T.submit}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-fog">
            <VerdeSpark className="h-3 w-3 text-mint" /> AI-assisted · Expert-underwritten
          </p>
        </motion.form>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-3">
          {T.steps.map((label, i) => (
            <div key={label} className="flex items-center justify-between border-b border-sand/70 pb-2.5" data-testid={`analysis-step-${i}`}>
              <span className={`text-xs font-medium ${i <= step ? "text-verde-800" : "text-fog/50"}`}>{label}</span>
              {i < step ? (
                <Check className="h-3.5 w-3.5 text-mint" strokeWidth={3} />
              ) : i === step ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-verde-700 border-t-transparent" />
              ) : (
                <span className="h-3.5 w-3.5 rounded-full border border-sand" />
              )}
            </div>
          ))}
          <div className="h-1 w-full overflow-hidden rounded-full bg-cream-dark">
            <motion.div
              className="h-full rounded-full bg-verde-800"
              animate={{ width: `${((step + 1) / T.steps.length) * 100}%` }}
              transition={{ duration: 0.6 }}
            />
          </div>
        </motion.div>
      )}
    </div>
  );
}
